// Sign in with Apple — Capacitor + Firebase bridge.
//
// Wired into AuthContext.loginWithApple(). Native iOS uses the
// @capacitor-community/apple-sign-in plugin; web falls back to Firebase's
// signInWithPopup(OAuthProvider('apple.com')). On Android we throw a
// "not supported" error — Apple guidelines forbid showing the button there.
//
// Feature-flagged behind REACT_APP_APPLE_SIGN_IN_ENABLED until the Apple
// Developer account + Service ID are configured.
import { OAuthProvider, signInWithPopup, signInWithCredential } from 'firebase/auth';
import { SignInWithApple } from '@capacitor-community/apple-sign-in';
import { auth } from './firebase';
import { isNative, platform } from './native';

export const APPLE_ENABLED =
  process.env.REACT_APP_APPLE_SIGN_IN_ENABLED === 'true';

/** Should the Apple button be visible at all on the current device? */
export function shouldShowAppleButton() {
  if (!APPLE_ENABLED) return false;
  if (isNative()) return platform() === 'ios';
  // Show on web for desktop/Safari/Mac users — Firebase popup handles them.
  return true;
}

function genNonce() {
  const a = new Uint8Array(16);
  (window.crypto || window.msCrypto).getRandomValues(a);
  return Array.from(a).map((b) => b.toString(16).padStart(2, '0')).join('');
}

async function sha256Hex(s) {
  const enc = new TextEncoder().encode(s);
  const buf = await window.crypto.subtle.digest('SHA-256', enc);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Run the platform-appropriate Apple Sign-In flow and exchange the result
 * for a Firebase credential. Returns the FirebaseUser on success.
 *
 * Throws on cancel or error — callers should `try/catch` and surface a toast.
 */
export async function signInWithApple() {
  if (!APPLE_ENABLED) {
    throw new Error('Apple Sign-In is not yet enabled — please use Google or email.');
  }

  // ── Native iOS path ──────────────────────────────────────────────────────
  if (isNative() && platform() === 'ios') {
    const rawNonce = genNonce();
    const hashedNonce = await sha256Hex(rawNonce);
    const result = await SignInWithApple.authorize({
      clientId: process.env.REACT_APP_APPLE_SERVICE_ID,
      redirectURI: process.env.REACT_APP_APPLE_REDIRECT_URI,
      scopes: 'email name',
      // Apple requires the SHA-256 of the rawNonce for the JWT to verify.
      nonce: hashedNonce,
      state: rawNonce,
    });
    const idToken = result?.response?.identityToken;
    if (!idToken) throw new Error('Apple Sign-In returned no identity token');
    const provider = new OAuthProvider('apple.com');
    const credential = provider.credential({ idToken, rawNonce });
    const cred = await signInWithCredential(auth, credential);
    return cred.user;
  }

  // ── Native Android path — not allowed by Apple guidelines ────────────────
  if (isNative() && platform() === 'android') {
    throw new Error('Sign in with Apple is iOS-only on mobile.');
  }

  // ── Web path (desktop / Mac Safari) ──────────────────────────────────────
  const provider = new OAuthProvider('apple.com');
  provider.addScope('email');
  provider.addScope('name');
  const cred = await signInWithPopup(auth, provider);
  return cred.user;
}
